'use client';

import { useRouter, useSearchParams } from "next/navigation";
import { cancelSubscription, getCouponInfo, updatePaygAutoRenewSettings, type CouponInfo, type PaygAutoRenewSettings, type PaygPricing, type TrialStatus } from "@/services/billing";
import { extendTrial } from "@/services/user";
import { PricingPlan } from "@/app/types/PricingPlan";
import { Subscription } from "@/app/types/Subscription";
import PricingTable from "@/components/billing/pricing-table";
import CancelProPlanModal from "@/components/ui/cancel-pro-plan-modal";
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/buttons/button";
import PercentageIcon from "@/assets/icons/percentage.svg";
import { analytics } from "@/app/analytics";
import { ArrowLeft } from "lucide-react";
import { normalizePaygAmountCents } from "@/lib/payg-pricing";
import { getBillingMode, setBillingMode } from "@/lib/billing-query-params";
import { toast } from "sonner";

export default function BillingClient({
    pricing,
    subscription,
    onDiscountApplied,
    effectiveCouponCode,
    trialStatus,
    utmMedium,
    paygPricing,
    paygPricingError,
    paygAutoRenewSettings,
    canUsePayg,
}: {
    pricing: { planType: 'starter' | 'pro' | 'advanced', credits: number, price: number, interval: string, originalPrice: number, discountPercentage: number | null, hasDiscount?: boolean }[];
    subscription: Subscription | null;
    onDiscountApplied: () => Promise<{ success: boolean }>;
    effectiveCouponCode?: string;
    trialStatus?: TrialStatus | null;
    utmMedium?: string | null;
    paygPricing?: PaygPricing | null;
    paygPricingError?: boolean;
    paygAutoRenewSettings?: PaygAutoRenewSettings | null;
    canUsePayg: boolean;
}) {
    const router = useRouter();
    const searchParams = useSearchParams();
    const [showModal, setShowModal] = useState(false);
    const hasUtmInUrl = !!searchParams.get('utm_medium');
    const [discountApplied, setDiscountApplied] = useState(searchParams.get('discount') === 'true' || hasUtmInUrl);
    const [couponInfo, setCouponInfo] = useState<CouponInfo | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
    const [renewalSettings, setRenewalSettings] = useState(paygAutoRenewSettings);

    // Get initial values from URL
    const initialPlanType = searchParams.get('planType') ? searchParams.get('planType') as 'starter' | 'pro' | 'advanced' : undefined;
    const initialIsYearly = searchParams.get('isYearly') ? searchParams.get('isYearly') === 'true' : undefined;
    const requestedMode = searchParams.has('billingMode') || searchParams.has('isYearly')
        ? getBillingMode(searchParams)
        : 'annual';
    const initialMode = !canUsePayg && requestedMode === 'payg'
        ? 'annual'
        : requestedMode;
    const initialPaygAmountCents = normalizePaygAmountCents(
        searchParams.get('amountCents') ??
            searchParams.get('amount') ??
            paygAutoRenewSettings?.topUpAmountCents,
        paygPricing,
    ) ?? 0;
    const initialAutoRenew = searchParams.get('autoRenew') === 'true'
        || (!!paygAutoRenewSettings?.enabled && !searchParams.has('autoRenew'));
    const paygStateRef = useRef({
        amountCents: initialPaygAmountCents,
        autoRenew: initialAutoRenew,
    });

    useEffect(() => {
        paygStateRef.current = {
            amountCents: initialPaygAmountCents,
            autoRenew: initialAutoRenew,
        };
    }, [initialAutoRenew, initialPaygAmountCents]);

    useEffect(() => {
        const fetchCouponInfo = async () => {
            if (effectiveCouponCode) {
                try {
                    const info = await getCouponInfo(effectiveCouponCode);
                    if (info.id) {
                        setCouponInfo(info);
                    }
                } catch (error) {
                    console.error('Failed to fetch coupon info:', error);
                }
            }
        };
        setIsLoading(true);
        fetchCouponInfo();
        setIsLoading(false);
    }, [effectiveCouponCode]);

    useEffect(() => {
        if (searchParams.get('discount') === 'true' && window.innerWidth < 640) {
            const proSection = document.getElementById('pro-plan-section');
            if (proSection) {
                proSection.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }, [searchParams]);

    const isTrialing = subscription?.status === 'trialing';

    // Upgrades are a team-level action — any team member (owner or invitee) can
    // pay for the team plan. The webhook routes the new subscription to the
    // payer's existing team (preferring a team they joined over an
    // auto-created solo team) and makes the payer the billing contact.
    const changePlan = async (plan: PricingPlan) => {
        const params = new URLSearchParams();
        params.set('planId', plan.planId);
        params.set('planType', plan.planType);
        params.set('billingMode', plan.isYearly ? 'annual' : 'monthly');
        params.set('isYearly', plan.isYearly.toString());

        const selectedPricing = pricing.find(p => p.planType === plan.planType && p.interval === (plan.isYearly ? 'year' : 'month'));
        if (discountApplied && couponInfo?.id && selectedPricing?.hasDiscount) {
            params.set('stripeCouponId', couponInfo.id);
        }

        const effectiveUtmMedium = searchParams.get('utm_medium') || utmMedium;
        if (effectiveUtmMedium) {
            params.set('utm_medium', effectiveUtmMedium);
        }

        router.push(`/earn-credits/billing/checkout?${params.toString()}`);
    };

    const handlePlanUpdate = (planType: 'starter' | 'pro' | 'advanced', isYearly: boolean) => {
        const params = new URLSearchParams(searchParams);
        params.delete('billingMode');
        params.delete('amountCents');
        params.delete('autoRenew');
        params.set('planType', planType);
        setBillingMode(params, isYearly ? 'annual' : 'monthly');
        params.set('isYearly', isYearly.toString());
        // analytics.getTrackerInstance().planUpdate({
        //     planType: planType,
        //     isYearly: isYearly,
        // });
        if (discountApplied) {
            params.set('discount', 'true');
        }
        router.push(`/earn-credits/billing?${params.toString()}`);
    };

    const handleModeChange = (mode: 'annual' | 'monthly' | 'payg') => {
        const params = new URLSearchParams(searchParams);
        params.set('billingMode', mode);
        if (mode === 'payg') {
            params.delete('isYearly');
            params.set('amountCents', String(paygStateRef.current.amountCents));
            params.set('autoRenew', String(paygStateRef.current.autoRenew));
        } else {
            params.set('isYearly', String(mode === 'annual'));
            params.delete('amountCents');
            params.delete('autoRenew');
        }
        analytics.getTrackerInstance().track?.({
            event_type: mode === 'payg' ? 'PAYG Tab Selected' : 'Billing Tab Selected',
            event_properties: { mode, amountCents: initialPaygAmountCents },
        } as any);
        router.push(`/earn-credits/billing?${params.toString()}`);
    };

    const handlePaygCheckout = (amountCents: number, autoRenew: boolean) => {
        analytics.getTrackerInstance().track?.({
            event_type: 'PAYG Checkout Started',
            event_properties: { amountCents, autoRenew },
        } as any);
        const params = new URLSearchParams();
        params.set('checkoutType', 'payg');
        params.set('amountCents', String(amountCents));
        params.set('autoRenew', String(autoRenew));
        router.push(`/earn-credits/billing/checkout?${params.toString()}`);
    };

    const handleAutoRenewChange = async (enabled: boolean, topUpAmountCents: number) => {
        if (!renewalSettings) return true;
        try {
            setRenewalSettings(
                await updatePaygAutoRenewSettings({ enabled, topUpAmountCents }),
            );
            return true;
        } catch (error) {
            console.error(`Failed to ${enabled ? 'enable' : 'disable'} Auto Renewal:`, error);
            toast.error(
                enabled
                    ? 'Unable to enable Auto Renewal. Please try again.'
                    : 'Auto Renewal is still active. Please try again.',
            );
            return false;
        }
    };

    const handlePaygStateChange = (amountCents: number, autoRenew: boolean) => {
        paygStateRef.current = { amountCents, autoRenew };
        const params = new URLSearchParams(searchParams);
        params.set('billingMode', 'payg');
        params.set('amountCents', String(amountCents));
        params.set('autoRenew', String(autoRenew));
        const nextUrl = `/earn-credits/billing?${params.toString()}`;
        if (`${window.location.pathname}${window.location.search}` !== nextUrl) {
            window.history.replaceState(window.history.state, '', nextUrl);
        }
    };

    const handleDowngrade = async () => {
        analytics.getTrackerInstance().downgradePro();
        setShowModal(true);
    };

    const handleExtendTrial = async () => {
        analytics.getTrackerInstance().extendTrial();
        await extendTrial();
        setShowModal(false);
        router.refresh();
    };

    const handleCancelSubscription = async () => {
        analytics.getTrackerInstance().cancelSubscription();
        await cancelSubscription();
        setShowModal(false);
        router.refresh();
    };

    const handleApplyDiscount = async () => {
        setIsApplyingDiscount(true);
        analytics.getTrackerInstance().discountApplied({
            utmMedium: effectiveCouponCode || '',
        });
        const result = await onDiscountApplied();
        if (result.success) {
            const params = new URLSearchParams(searchParams);
            params.set('discount', 'true');
            router.push(`/earn-credits/billing?${params.toString()}`);
            setDiscountApplied(true);
        }
    };

    // Reset isApplyingDiscount when pricing changes
    useEffect(() => {
        setIsApplyingDiscount(false);
    }, [pricing]);

    const handleBack = () => {
        router.back();
    };

    return (
        <>
            <PricingTable
                pricing={pricing}
                paygPricing={paygPricing}
                paygPricingError={paygPricingError}
                paygAutoRenewSettings={renewalSettings}
                showPayg={canUsePayg}
                onPaygAutoRenewChange={handleAutoRenewChange}
                onPaygStateChange={handlePaygStateChange}
                changePlan={changePlan}
                downgrade={handleDowngrade}
                currentSubscription={isTrialing ? undefined : (subscription || undefined)}
                publicMode={false}
                discountApplied={discountApplied}
                onPlanUpdate={handlePlanUpdate}
                onPaygCheckout={handlePaygCheckout}
                onModeChange={handleModeChange}
                initialPlanType={initialPlanType}
                initialIsYearly={initialIsYearly}
                initialMode={initialMode}
                initialPaygAmountCents={initialPaygAmountCents}
                initialAutoRenew={initialAutoRenew}
                isLoading={isLoading || isApplyingDiscount}
            />
            {canUsePayg && searchParams.get('payg') && (
                <p className="mx-auto max-w-[560px] px-4 text-center text-sm text-red-700" role="alert">
                    {{
                        failed: 'Your PAYG payment was declined or could not be completed. Choose an amount and try again.',
                        expired: 'Your PAYG checkout expired before payment completed. Start a new checkout when you are ready.',
                        pending: 'Your payment is still being confirmed. This page will show your credits once Stripe finishes processing it.',
                        'missing-session': 'We could not identify that checkout. Start a new PAYG purchase from this page.',
                        error: 'We could not confirm your PAYG payment. Refresh once, then contact support if it is still pending.',
                    }[searchParams.get('payg') ?? ''] ??
                        'Your PAYG checkout needs attention. Start a new purchase or contact support.'}
                </p>
            )}

            {showModal && subscription && (
                <CancelProPlanModal
                    onExtendTrial={handleExtendTrial}
                    onClose={() => setShowModal(false)}
                    subscription={subscription}
                    onCancel={handleCancelSubscription}
                />
            )}
        </>
    );
}
